import {
  Anchor,
  Box,
  Button,
  Flex,
  Group,
  List,
  Text,
  Title,
} from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import RoutesString from "pages/routesString";
import ApprovalBox from "components/common/box/ApprovalBox";
import { v4 as uuidv4 } from "uuid";
import { useDisclosure } from "@mantine/hooks";
import OtpModal from "pages/term-deposit/term-deposit-detail/modal/OtpModal";
import useAuthentication from "stores/AuthenticationStore/authentication";
import { get } from "lodash";
import { PDFModal } from "pages/term-deposit/term-deposit-detail/modal/PdfModal";

export const DepositSettlement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // =======================
  // State
  // =======================
  const [otp, setOtp] = useState("");
  const [onNextOtpAction, setOnNextOtpAction] = useState<() => void>(
    () => () => {}
  );
  const [stateAuth] = useAuthentication();

  // =======================
  // Modal control
  // =======================
  const [otpModalOpened, { open: openOtpModal, close: closeOtpModal }] =
    useDisclosure(false);
  const [
    isOpenContractInformation,
    { open: openContractInformation, close: closeContractInformation },
  ] = useDisclosure(false);

  const handleCloseOtpModal = () => {
    setOtp("");
    closeOtpModal();
  };

  // =======================
  // Data
  // =======================
  const phoneNumber = get(stateAuth, ["customer", "mobilePhone"], "");

  const requestInfo = [
    { label: t("FULL_NAME"), value: "NGUYEN VAN A", color: "#2E2E2E" },
    { label: t("DATE_OF_BIRTH"), value: "01/01/2000", color: "#0E74DA" },
    {
      label: t("ID_CARD_/_CITIZEN_ID"),
      value: "097097112233",
      color: "#2E2E2E",
    },
    { label: t("ISSUE_DATE"), value: "07/11/2025", color: "#2E2E2E" },
    { label: t("TELEPHONE"), value: "098123456", color: "#2E2E2E" },
    { label: t("PERMANENT_ADDRESS"), value: "123 NKKN", color: "#2E2E2E" },
  ];

  const loanDemand = [
    { label: t("TOTAL_CAPITAL_REQUIREMENT"), value: "6,000,000 VND" },
    { label: t("LOAN_AMOUNT"), value: "5,000,000 VND" },
    { label: t("EQUITY_CAPITAL"), value: "1,000,000 VND" },
    { label: t("DISBURSEMENT_ACCOUNT"), value: "060100001234" },
    // VersionEn: 4 giá trị dưới đây là số liệu mockup (màn chưa nối API) nhưng vẫn hiện ra
    // nên đưa vào từ điển; nhãn thì đã dùng key có sẵn từ trước.
    { label: t("LOAN_TERM"), value: `${t("MOCK_24_MONTHS")}` },
    { label: t("INTEREST_RATE"), value: `${t("MOCK_RATE_9_5_PER_MONTH")}` },
    { label: t("INTEREST_CALCULATION_METHOD"), value: `${t("MOCK_EQUAL_INSTALMENT")}` },
    { label: t("LOAN_PURPOSE"), value: `${t("MOCK_CONSUMPTION")}` },
  ];

  // =======================
  // Render Section
  // =======================
  const renderInfoSection = (
    title: string,
    data: { label: string; value: string; color?: string }[]
  ) => (
    <Flex direction="column" gap="md" fz="md">
      <Title order={4}>{title}</Title>
      <Flex direction="column" gap="lg">
        {data.map((item, index) => (
          <Flex key={uuidv4()} gap="md">
            <Text w={300}>{item.label}</Text>
            <Text fw={600} c={item.color || "#2E2E2E"}>
              {item.value}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );

  // =======================
  // Action Handlers
  // =======================

  // --- Accept
  const handleAcceptSuccess = () => {
    handleCloseOtpModal();
    navigate(`${RoutesString.TermLoanSettlementResponse}?q=SUCCESS`);
  };

  const handleAccept = () => {
    setOnNextOtpAction(() => handleAcceptSuccess);
    openOtpModal();
  };

  // --- Refund
  const handleRefundSuccess = () => {
    handleCloseOtpModal();
    navigate(`${RoutesString.TermLoanSettlementResponse}?q=REFUND`);
  };

  const handleRefund = () => {
    setOnNextOtpAction(() => handleRefundSuccess);
    openOtpModal();
  };

  // --- Refuse
  const handleRefuseSuccess = () => {
    handleCloseOtpModal();
    navigate(`${RoutesString.TermLoanSettlementResponse}?q=REFUSE`);
  };

  const handleRefuse = () => {
    setOnNextOtpAction(() => handleRefuseSuccess);
    openOtpModal();
  };

  return (
    <>
      <Flex gap="xl" direction="column" mt="sm">
        <Flex gap="sm" align="center">
          <Box
            style={{
              background:
                "linear-gradient(265.56deg, #246CF9 -0.27%, #1E68F6 -0.26%, #0047D0 98.59%)",
              width: 3,
              height: 32,
              borderRadius: "18px",
            }}
          />

          <Title order={3}>{t("FINALIZE_DEPOSIT")}</Title>
        </Flex>
        <Box
          pb={40}
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "20px",
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: "20px",
              padding: "10px 13px",
              flexDirection: "column",
              flex: 1,
              maxWidth: "1160px",
              backgroundColor: "#FDFDFD",
              borderRadius: "8px",
            }}
          >
            <Flex
              gap="lg"
              style={{ flex: 1, borderRadius: "1rem" }}
              bg="#FFFFFF"
              p="md"
              direction="column"
            >
              {renderInfoSection(t("REQUEST_INFORMATION"), requestInfo)}
              {renderInfoSection(t("LOAN_DEMAND"), loanDemand)}
              <Text fw={700} fz={14}>
                III. {t("COMMITMENTS_OF_THE_LOAN_APPLICANT")}
              </Text>
              <List listStyleType="disc" pl={10}>
                <List.Item fz={14} fw={400}>
                  {t("COMMIT_INFO_TRUE")}{" "}
                </List.Item>
                <List.Item fz={14} fw={400}>
                  {t("COMMIT_INCOME_SUFFICIENT")}{" "}
                </List.Item>
              </List>
              <Text fw={700} fz={14}>
                III. {t("COMMITMENTS_OF_THE_LOAN_APPLICANT")}
              </Text>
              <Anchor href="https://mantine.dev/" target="_blank">
                {t("VIEW_HERE")}
              </Anchor>
            </Flex>
          </Box>
          <ApprovalBox
            ruleId="4325325"
            authority="1"
            approver="TungNT"
            authMode="SmartOTP"
            onAuthModeChange={(newMode) =>
              console.log("Selected mode:", newMode)
            }
          />

          {/* )} */}
        </Box>
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            right: 0,
            zIndex: 2,
            padding: "24px 32px",
            backgroundColor: "#fff",
            boxShadow: "0px -2px 8px rgba(0, 0, 0, 0.05)",
            width: "calc(100% - var(--mantine-navbar-width, 0px))",
          }}
        >
          <Group position="right">
            <>
              <Button
                sx={{
                  color: "#525866",
                  backgroundColor: "#F4F4F6",
                  "&:hover": { backgroundColor: "#F4F4F6" },
                  "&:active": { backgroundColor: "#F4F4F6" },
                  "&:focus": { outline: "none", boxShadow: "none" },
                  padding: "10px 24px",
                  borderRadius: "8px",
                }}
                onClick={handleRefund}
              >
                {t("REFUND")}
              </Button>
              <Button
                sx={{
                  color: "#E6492D",
                  backgroundColor: "#FEEFEE",
                  "&:hover": { backgroundColor: "#FEEFEE" },
                  "&:active": { backgroundColor: "#FEEFEE" },
                  "&:focus": { outline: "none", boxShadow: "none" },
                  padding: "10px 24px",
                  borderRadius: "8px",
                }}
                onClick={handleRefuse}
              >
                {t("REJECT")}
              </Button>
              <Button
                sx={{
                  color: "#FDFDFD",
                  backgroundColor: "#2A81D0",
                  "&:hover": { backgroundColor: "#2A81D0" },
                  "&:active": { backgroundColor: "#2A81D0" },
                  "&:focus": { outline: "none", boxShadow: "none" },
                  padding: "10px 24px",
                  borderRadius: "8px",
                }}
                onClick={openContractInformation}
              >
                {t("ACCEPT")}
              </Button>
            </>
          </Group>
        </Box>
      </Flex>
      <PDFModal
        opened={isOpenContractInformation}
        onClose={closeContractInformation}
        fileUrl="/pdf/file.pdf"
        onNext={handleAccept}
      />
      <OtpModal
        opened={otpModalOpened}
        close={handleCloseOtpModal}
        otp={otp}
        setOtp={setOtp}
        onNext={onNextOtpAction}
        phoneNumber={phoneNumber}
      />
    </>
  );
};
